import { useEffect, useState } from 'react'
import { getAuditLogs } from '../../services/api'
import { AuditLog } from '../../types'
import { ClipboardList, Filter, Calendar, User, Building } from 'lucide-react'

export default function AuditLogView() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getAuditLogs()
      .then(res => setLogs(res.data.logs))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const filtered = logs.filter(log =>
    log.action.toLowerCase().includes(filter.toLowerCase()) ||
    log.user_email?.toLowerCase().includes(filter.toLowerCase()) ||
    log.client_name?.toLowerCase().includes(filter.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-800">Audit Log</h2>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Filter actions, users..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input pl-9 w-64"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(log => (
                <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar size={12} className="mr-1.5 text-gray-400" />
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      log.action.includes('created') ? 'bg-green-50 text-green-600' :
                      log.action.includes('deleted') ? 'bg-red-50 text-red-600' :
                      log.action.includes('failed') ? 'bg-red-50 text-red-600' :
                      log.action.includes('login') ? 'bg-blue-50 text-blue-600' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-navy-700">
                    <div className="flex items-center">
                      <User size={12} className="mr-1.5 text-gray-400" />
                      {log.user_email || 'System'}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Building size={12} className="mr-1.5 text-gray-400" />
                      {log.client_name || 'N/A'}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    {log.entity_type && log.entity_id ? (
                      <span className="text-xs bg-navy-50 text-navy-600 px-2 py-1 rounded">
                        {log.entity_type} #{log.entity_id}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
            <p>No audit logs found</p>
          </div>
        )}
      </div>
    </div>
  )
}
